import { Component, computed, inject, input, output, signal, viewChild, viewChildren } from '@angular/core';
import { delay, Waiter, withIndex } from '@gametailors/web-core/misc';
import { CompressibleCardsService } from '../../components/rid-compressable-klank-card/compressible-cards.service';
import { RidCompressableKlankCard } from '../../components/rid-compressable-klank-card/rid-compressable-klank-card';
import { RidCorrectAnswerModal } from '../../components/rid-correct-answer-modal/rid-correct-answer-modal';
import { RidFunctionalSyllabicKeyboard, TRule } from '../../components/rid-functional-syllabic-keyboard/rid-functional-syllabic-keyboard';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import {
  MORPHEME_SUFFIXES_B,
  RidMorphemeKeyboard,
  stripMorphemeDashes,
  TPrefixValue,
  TSuffixValue,
} from '../../components/rid-morpheme-keyboard/rid-morpheme-keyboard';
import { RidOption, TOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSyllabicCard } from '../../components/rid-syllabic-card/rid-syllabic-card';
import { RidSyllabicKeyboardUtils, TKey, TKeyValue } from '../../components/rid-syllabic-keyboard/rid-syllabic-keyboard.utils';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { PronunciationService } from '../../services/pronunciation.service';
import { AudioEffectPlayer } from '../../utils/audio.utils';

export interface TMorphemeBoardPrompt {
  prompt: TOption;
  prefix?: TPrefixValue;
  suffix?: TSuffixValue;
  // Shown above the input; can't be derived from `center` (e.g. verlossen: center is l-o-ss, stem is 'los').
  stem: string;
  // May contain rule keys (e.g. ['z', 'o', '+', 'n'] for zonnig), which are applied automatically like in klankbord+.
  center: TKeyValue[];
}

const CENTER_LAYOUT = {
  KLINKERS: 'ENABLED',
  LANGE_KLINKERS: 'ENABLED',
  KORTE_LANGE_KLINKERS: 'ENABLED',
  SPACE: 'HIDDEN',
  DUBBELE_KLINKERS: 'ENABLED',
  DRIEDUBBELE_KLINKERS: 'ENABLED',
  VIERDUBBELE_KLINKERS: 'ENABLED',
  MEDEKLINKERS: 'ENABLED',
  EXOTEN: 'HIDDEN',
  SJWAS: 'HIDDEN',
  KLANKVOET: 'HIDDEN',
  RULE: 'HIDDEN',
} as const;

@Component({
  selector: 'rid-morpheme-board',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidOption,
    RidTextInput,
    RidVerticalSpacer,
    RidCompressableKlankCard,
    RidSyllabicCard,
    RidOverlayFeedback,
    RidModalBackground,
    RidCorrectAnswerModal,
    RidFunctionalSyllabicKeyboard,
    RidMorphemeKeyboard,
    RidSfxDirective,
  ],
  templateUrl: './rid-morpheme-board.html',
  host: {
    class: 'block',
  },
})
export class RidMorphemeBoard {
  private readonly $pronunciationService = inject(PronunciationService);
  private readonly $compressibleCardsService = inject(CompressibleCardsService);

  protected readonly centerLayout = CENTER_LAYOUT;
  protected readonly stripDashes = stripMorphemeDashes;

  prompts = input.required<TMorphemeBoardPrompt[]>();

  finished = output<void>();

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  keyboardComp = viewChild.required<RidFunctionalSyllabicKeyboard>('keyboardComp');
  syllabicCard = viewChildren<RidSyllabicCard>('syllabicCard');
  prefixCard = viewChild<RidCompressableKlankCard>('prefixCard');
  suffixCard = viewChild<RidCompressableKlankCard>('suffixCard');

  // Whether the prompt has finished its initial presentation (audio played) — gates the keyboard's visibility.
  protected revealed = signal(false);
  // Whether we're mid-feedback-flash (correct/incorrect overlay) — gates interactivity without hiding anything.
  isProvidingFeedback = signal(true);
  private morphemesAcknowledged = signal(false);

  protected confirmedPrefix = signal<TPrefixValue | undefined>(undefined);
  protected confirmedSuffix = signal<TSuffixValue | undefined>(undefined);
  protected displayedPrefix = signal<TPrefixValue | undefined>(undefined);
  protected displayedSuffix = signal<TSuffixValue | undefined>(undefined);

  // Group B suffixes change the center (e.g. slaap -> slapen), which is signalled by colouring the center input the same way.
  protected isStemChangingSuffix = computed(() => {
    const suffix = this.displayedSuffix();
    return suffix !== undefined && MORPHEME_SUFFIXES_B.includes(suffix);
  });

  protected suffixCardClass = computed(() => (this.isStemChangingSuffix() ? 'font-bold text-morpheme-stem' : 'font-bold text-blue-500'));

  promptIndex = signal(0);
  centerIndex = signal(0);
  progress = signal(0);
  attempts = signal<Map<number, number>>(new Map());
  revelation = signal<TKey[] | undefined>(undefined);

  readonly revealWaiter = new Waiter();

  protected rawPrompt = computed(() => this.prompts()[this.promptIndex()]);

  // The center as it's typed: a dubbelzetter ('+') expects its medeklinker twice.
  protected center = computed(() => {
    const raw = this.rawPrompt().center;
    const center: TKeyValue[] = [];

    for (const [key, i] of withIndex(raw)) {
      center.push(key);

      if (key === '+') {
        center.push(raw[i + 1]);
      }
    }

    return center;
  });

  protected step = computed<'MORPHEMES' | 'CENTER'>(() => {
    const prompt = this.rawPrompt();
    const needsMorphemes = prompt.prefix !== undefined || prompt.suffix !== undefined;

    return needsMorphemes && !this.morphemesAcknowledged() ? 'MORPHEMES' : 'CENTER';
  });

  protected prefixKeyboardDisabled = computed(
    () => this.isProvidingFeedback() || this.step() !== 'MORPHEMES' || this.confirmedPrefix() !== undefined,
  );

  protected suffixKeyboardDisabled = computed(
    () => this.isProvidingFeedback() || this.step() !== 'MORPHEMES' || this.confirmedSuffix() !== undefined,
  );

  protected validateCenter(key: TKey): boolean {
    const value = this.center()[this.centerIndex()];
    return key.value === value;
  }

  protected async presentOption(option: RidOption) {
    await option.present();

    this.isProvidingFeedback.set(false);
    this.revealed.set(true);

    if (this.step() === 'CENTER') {
      this.pressPendingRule();
    }
  }

  // Rules aren't typed by the player: when the next expected key is a rule, apply it on their behalf.
  private pressPendingRule() {
    const next = this.center().at(this.centerIndex());
    const nextKey = next ? RidSyllabicKeyboardUtils.convertValueToKey(next) : undefined;

    if (nextKey?.type === 'RULE') {
      this.keyboardComp().press(nextKey);
    }
  }

  protected async onPrefixPressed(value: TPrefixValue) {
    if (this.isProvidingFeedback() || this.confirmedPrefix() !== undefined) return;

    this.displayedPrefix.set(value);

    if (this.rawPrompt().prefix === value) {
      this.confirmedPrefix.set(value);
      this.checkMorphemesCompletion();
      return;
    }

    this.isProvidingFeedback.set(true);
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();
    await delay(500);
    this.displayedPrefix.set(undefined);
    this.isProvidingFeedback.set(false);
  }

  protected async onSuffixPressed(value: TSuffixValue) {
    if (this.isProvidingFeedback() || this.confirmedSuffix() !== undefined) return;

    this.displayedSuffix.set(value);

    if (this.rawPrompt().suffix === value) {
      this.confirmedSuffix.set(value);
      this.checkMorphemesCompletion();
      return;
    }

    this.isProvidingFeedback.set(true);
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();
    await delay(500);
    this.displayedSuffix.set(undefined);
    this.isProvidingFeedback.set(false);
  }

  private checkMorphemesCompletion() {
    const prefixDone = this.rawPrompt().prefix === undefined || this.confirmedPrefix() !== undefined;
    const suffixDone = this.rawPrompt().suffix === undefined || this.confirmedSuffix() !== undefined;

    if (prefixDone && suffixDone) {
      void this.onMorphemesCompleted();
    }
  }

  private async onMorphemesCompleted() {
    this.isProvidingFeedback.set(true);
    void AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(750);

    this.morphemesAcknowledged.set(true);
    this.centerIndex.set(0);
    this.isProvidingFeedback.set(false);
    this.pressPendingRule();
  }

  protected async onCenterInsert() {
    this.centerIndex.update((i) => i + 1);

    if (this.centerIndex() >= this.center().length) {
      await this.onCenterCompleted();
      return;
    }

    this.pressPendingRule();
  }

  protected onCenterApplyRule(rule: TRule) {
    // MEDEKLINKER is the keyboard's follow-up to a dubbelzetter, not a key in the center.
    if (rule.name !== 'MEDEKLINKER') {
      this.centerIndex.update((i) => i + 1);
    }
  }

  protected async onCenterIncorrect() {
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();
    this.attempts.update((a) => new Map(a).set(this.centerIndex(), (a.get(this.centerIndex()) ?? 0) + 1));

    if (Array.from(this.attempts().values()).reduce((acc, cur) => acc + cur, 0) >= 5) {
      await this.reveal();
      this.goToNextPrompt();
    }
  }

  private async onCenterCompleted() {
    this.isProvidingFeedback.set(true);

    void AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(750);

    const keyboard = this.keyboardComp();
    const isCompressible = keyboard.content().every((k) => k.type === 'KLANK' || k.type === 'EXCEPTION' || k.type === 'SPACE');
    if (isCompressible) {
      const prefixCard = this.prefixCard();
      const suffixCard = this.suffixCard();
      const cards = [
        ...(prefixCard ? [prefixCard] : []),
        ...this.syllabicCard().flatMap((c) => c.compressable()),
        ...(suffixCard ? [suffixCard] : []),
      ];
      this.$compressibleCardsService.compress(cards);
    }

    const rawPrefix = this.rawPrompt().prefix;
    const rawSuffix = this.rawPrompt().suffix;
    const prefix = rawPrefix ? this.stripDashes(rawPrefix) : '';
    const suffix = rawSuffix ? this.stripDashes(rawSuffix) : '';
    await this.$pronunciationService.tryPronounce(prefix + keyboard.getRawText() + suffix);

    await delay(250);

    this.goToNextPrompt();
  }

  private async reveal() {
    this.revelation.set(
      this.center()
        .map((v) => RidSyllabicKeyboardUtils.convertValueToKey(v))
        .filter((k) => k.type !== 'RULE'),
    );

    await this.revealWaiter.wait();

    this.revelation.set(undefined);
  }

  private goToNextPrompt() {
    const isLast = this.promptIndex() + 1 >= this.prompts().length;
    if (isLast) {
      this.finished.emit();
      return;
    }

    this.promptIndex.update((i) => i + 1);
    this.centerIndex.set(0);
    this.progress.update((p) => p + 1);
    this.attempts.set(new Map());
    this.morphemesAcknowledged.set(false);
    this.confirmedPrefix.set(undefined);
    this.confirmedSuffix.set(undefined);
    this.displayedPrefix.set(undefined);
    this.displayedSuffix.set(undefined);
    this.revealed.set(false);
    this.isProvidingFeedback.set(true);
  }
}
